import {Box, Grid, InlineLink, Section, Text} from '@primer/react-brand/esm'
import {ArrowUpIcon} from '@primer/octicons-react'
import {renderWithoutParagraphs} from '../../../utils'
import type {OutroSectionProps} from './types'
import baseStyles from '../../../styles/base.module.css'
import overviewStyles from '../../OverviewTemplate.module.css'
import {FlexSuiteOutroCTA} from '../../../components/FlexSuiteOutroCTA'
import {FlexSuiteOutroCustomerStories} from '../../../components/FlexSuiteOutroCustomerStories'
import {FlexSuiteOutroFaqs, getCompleteFaqGroups} from '../../../components/FlexSuiteOutroFaqs'
import {FlexSuiteOutroResources} from '../../../components/FlexSuiteOutroResources'
import {useBrandedDivider} from '../../../hooks/useBrandedDivider'

export const OutroSection = ({
  settings,
  outroCustomerStories,
  outroCtaBanner,
  outroCtaHeading,
  outroCtaDescription,
  outroCtaOne,
  outroCtaTwo,
  outroIncludeResources,
  outroResourcesHeading,
  outroResources,
  outroFaqs,
  outroHeading,
  footnotes,
}: OutroSectionProps) => {
  const groupsWithFaqs = getCompleteFaqGroups(outroFaqs)

  const hasCustomerStories = outroCustomerStories && outroCustomerStories.length > 0
  const hasResources = Boolean(outroIncludeResources && outroResources?.length)
  const hasFootnotes = Boolean(footnotes?.length)
  const {brand} = settings?.fields || {}

  const {hasBrandedDivider, BrandedDivider} = useBrandedDivider({
    // Special case where the branded divider is not shown in the Outro section if it's a Copilot page.
    // We need to align on how to make this consistent across all Overview pages, but this is here for
    // now in order to support the current look of the two Overview template pages.
    enabled: brand !== 'Copilot',
  })
  const ctaNeedsBottomBorder = hasResources || hasBrandedDivider || groupsWithFaqs.length > 0 || hasFootnotes

  if (
    !hasCustomerStories &&
    !outroCtaBanner &&
    (!outroIncludeResources || !outroResources?.length) &&
    groupsWithFaqs.length === 0
  ) {
    return null
  }

  return (
    <Box marginBlockStart={48} className={baseStyles.greenBorderBottom}>
      <Section paddingBlockStart="none" className={baseStyles.gridFrameTop} paddingBlockEnd="none" fullWidth>
        {hasCustomerStories && <FlexSuiteOutroCustomerStories variant="logo-cards" stories={outroCustomerStories} />}

        {outroCtaBanner && (
          <FlexSuiteOutroCTA
            outroCtaHeading={outroCtaHeading}
            outroCtaDescription={outroCtaDescription}
            outroCtaOne={outroCtaOne}
            outroCtaTwo={outroCtaTwo}
            hasTopBorder={false}
            hasBottomBorder={ctaNeedsBottomBorder}
          />
        )}

        {outroIncludeResources && (
          <FlexSuiteOutroResources outroResourcesHeading={outroResourcesHeading} outroResources={outroResources} />
        )}

        {hasBrandedDivider ? (
          <Box className={baseStyles.gridFrameBottom}>
            <Grid>
              <Grid.Column>
                <BrandedDivider />
              </Grid.Column>
            </Grid>
          </Box>
        ) : null}

        {groupsWithFaqs.length > 0 && (
          <Box id="faq">
            <FlexSuiteOutroFaqs groups={groupsWithFaqs} heading={outroHeading} showSingleGroupHeadingWithHeading />
          </Box>
        )}

        {hasFootnotes && footnotes && (
          <Grid>
            <Grid.Column>
              <Box
                paddingBlockStart={{narrow: 'spacious', regular: 64}}
                paddingBlockEnd={{narrow: 'spacious', regular: 64}}
              >
                <Text as="div" size="100" variant="muted">
                  <ol className={overviewStyles.footnotesList}>
                    {footnotes.map((footnote, index) => (
                      <li key={footnote.id} id={`footnote-${index + 1}`} className={overviewStyles.footnotesItem}>
                        {renderWithoutParagraphs(footnote.content)}{' '}
                        <InlineLink
                          href="#pricing-section"
                          aria-label={`Back to pricing section (footnote ${index + 1})`}
                        >
                          <ArrowUpIcon size={12} />
                        </InlineLink>
                      </li>
                    ))}
                  </ol>
                </Text>
              </Box>
            </Grid.Column>
          </Grid>
        )}
      </Section>
    </Box>
  )
}
